import { chartConfig } from '@config/chart';

import { YScale } from './types';

// Centralized density filtering logic for all chart components
export function getVisibleStrikes(
  strikePrices: number[],
  yScale: YScale,
  innerHeight: number,
  selectedStrike?: number,
  _spotPrice?: number,
): Set<number> {
  const yDomain = yScale.domain();
  const priceRange = yDomain[1] - yDomain[0];
  const minPriceSpacing =
    priceRange * (chartConfig.strikeLabels.minSpacingPx / innerHeight);

  // Show strikes within the current price range + some buffer
  const minPrice =
    yDomain[0] - priceRange * chartConfig.strikeLabels.bufferPercent;
  const maxPrice =
    yDomain[1] + priceRange * chartConfig.strikeLabels.bufferPercent;

  const relevantStrikes = strikePrices.filter(
    strike => strike >= minPrice && strike <= maxPrice,
  );

  // Determine the best interval based on spacing needed
  const baseInterval = getBaseStrikeInterval(strikePrices);
  const targetInterval = getTargetInterval(baseInterval, minPriceSpacing);

  const strikesToShow = new Set<number>();

  // Always include selected strike
  if (selectedStrike && relevantStrikes.includes(selectedStrike)) {
    strikesToShow.add(selectedStrike);
  }

  // Add strikes that are multiples of the target interval
  for (const strike of relevantStrikes) {
    if (strike % targetInterval === 0) {
      strikesToShow.add(strike);
    }
  }

  // If we don't have enough strikes with clean intervals, fall back to spacing-based approach
  if (strikesToShow.size < 3) {
    const sortedRelevant = relevantStrikes.sort((a, b) => a - b);

    for (const currentStrike of sortedRelevant) {
      // Skip if already added
      if (strikesToShow.has(currentStrike)) {
        continue;
      }

      // Check if this strike would be too close to any already shown
      let tooClose = false;
      for (const shownStrike of strikesToShow) {
        const priceDistance = Math.abs(currentStrike - shownStrike);
        if (priceDistance < minPriceSpacing) {
          tooClose = true;
          break;
        }
      }

      if (!tooClose) {
        strikesToShow.add(currentStrike);
      }
    }
  }

  return strikesToShow;
}

// Helper to detect the base interval between strikes
function getBaseStrikeInterval(strikePrices: number[]): number {
  if (strikePrices.length < 2) {
    return chartConfig.strikeLabels.baseIntervalFallback;
  }

  const sorted = [...strikePrices].sort((a, b) => a - b);
  const intervals = new Map<number, number>();

  for (let i = 1; i < sorted.length; i++) {
    const interval = sorted[i] - sorted[i - 1];
    intervals.set(interval, (intervals.get(interval) ?? 0) + 1);
  }

  // Find the most common interval
  let maxCount = 0;
  let mostCommonInterval: number =
    chartConfig.strikeLabels.baseIntervalFallback;
  for (const [interval, count] of intervals) {
    if (count > maxCount) {
      maxCount = count;
      mostCommonInterval = interval;
    }
  }

  return mostCommonInterval;
}

// Helper to determine target interval based on spacing requirements
function getTargetInterval(baseInterval: number, minSpacing: number): number {
  // Common clean intervals
  const cleanIntervals = [
    baseInterval,
    baseInterval * 2,
    baseInterval * 4,
    baseInterval * 5,
    baseInterval * 10,
  ];

  // Find the smallest clean interval that meets our spacing requirement
  for (const interval of cleanIntervals) {
    if (interval >= minSpacing) {
      return interval;
    }
  }

  // If even the largest interval is too small, use it anyway
  return cleanIntervals[cleanIntervals.length - 1];
}

// Helper to check if a price is a visible strike (with floating point tolerance)
export function isVisibleStrike(
  price: number,
  visibleStrikes: Set<number>,
): boolean {
  for (const strike of visibleStrikes) {
    if (Math.abs(strike - price) < 0.0001) {
      return true;
    }
  }
  return false;
}
